import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { IFilterDefinition, ISelectFilterDefinition, ISelectFilterDefinitionOption } from '~content/services/FilterPanelService';
import { AdvancedFilterPanel, AdvancedFilterPanelProps } from '~src/spacesPlatform/components/Explorer/AdvancedFilterPanel';
import { isGroupSpace } from '~src/spacesPlatform/state/SpaceInfo';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { SiteRoleFilterHelper } from '../../../../siteManagement/helpers/SiteRoleFilterHelper';
import { ServerFilterClauses } from '../../../../ts/helpers/ServerFilterClauses';
import { IStringValuedFilterClause } from '../../../../ts/ServerApi/types/IFilterClause';
import { SiteRoles } from '../../../../ts/ServerApi/types/ISiteRole';
import { SiteRolesResource } from '../../../services/resources/SiteRoles';
import { SiteUsers } from '../../../services/resources/SiteUsers';
import { SiteUserDefinition } from '../../contentDefinition/SiteUserDefinition';


export interface SiteUsersAdvancedFilterPanelProps extends AdvancedFilterPanelProps {}

export interface SiteUsersAdvancedFilterPanelState {
  siteRoleOptionsWithCounts: ISelectFilterDefinitionOption<string>[];
}

// This function is a weird way to update filter definitions asynchronously
// since those definitions are created in the child component - AdvancedFilterPanel.
// See related comment there.
function transformFilterDefinitions(
  siteRoleOptionsWithCounts: ISelectFilterDefinitionOption<string>[],
): (filterDefinitions: IFilterDefinition<any>[]) => void {
  return (filterDefinitions: IFilterDefinition<any>[]) => {
    if (siteRoleOptionsWithCounts) {
      (filterDefinitions[0] as ISelectFilterDefinition<string>).options = siteRoleOptionsWithCounts;
    }
  };
}

export class SiteUsersAdvancedFilterPanel extends React.Component<SiteUsersAdvancedFilterPanelProps, SiteUsersAdvancedFilterPanelState> {

  private isUnmounted = false;
  private siteRoles: SiteRolesResource = undefined;
  private memoizedTransformFilterDefinitions = defaultMemoize(transformFilterDefinitions);

  public constructor(props: SiteUsersAdvancedFilterPanelProps) {
    super(props);
    this.siteRoles = SiteRolesResource.instance;
    this.state = {
      siteRoleOptionsWithCounts: null,
    };
  }

  public render() {
    const transformFilterDefinitions = this.memoizedTransformFilterDefinitions(
      this.state.siteRoleOptionsWithCounts
    );
    return <AdvancedFilterPanel {...this.props} transformFilterDefinitions={transformFilterDefinitions} />;
  }

  public componentDidMount() {
    this.updateSiteRoleCounts();
  }

  public componentDidUpdate(prevProps: SiteUsersAdvancedFilterPanelProps) {
    if (this.searchTerm(this.props) !== this.searchTerm(prevProps)) {
      this.updateSiteRoleCounts();
    }
  }

  public componentWillUnmount() {
    this.isUnmounted = true;
  }


  private updateSiteRoleCounts = async () => {
    let query = SiteUserDefinition.getBasicQuery();
    if (isGroupSpace(this.props.spaceInfo)) {
      query = query.addFilter((ServerFilterClauses.serverFilterClauses.inGroup(this.props.spaceInfo.item.id)));
    }
    if (this.searchTerm(this.props)) {
      query = query.addFilter(this.props.mixedContentFilterMap.search);
    }
    const userCountsByRole = await SiteUsers.instance.getUserCountsBySiteRole(query);

    let siteRolesToExclude: string[];
    if (FeatureFlags.hideServerAdminsFromUsersPageEnabled) {
      siteRolesToExclude = [SiteRoles.Guest, SiteRoles.ServerAdministrator];
    } else {
      siteRolesToExclude = [SiteRoles.Guest];
    }
    const siteRoleOptionsWithCounts = SiteRoleFilterHelper.siteRoleOptions(
      _.without(this.siteRoles.allOptions(), ...siteRolesToExclude),
      userCountsByRole
    );

    !this.isUnmounted && this.setState({ siteRoleOptionsWithCounts });
  }

  private searchTerm = (props: SiteUsersAdvancedFilterPanelProps) => {
    return (props.mixedContentFilterMap.search
      && (props.mixedContentFilterMap.search as IStringValuedFilterClause).value)
      || '';
  }
}
